import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";
import { useSave } from "@ncp/ui";
import type { BaseBody, DefinitionSummary } from "../generic/api";

export type RecordFieldType = "text" | "longText" | "number" | "money" | "date" | "boolean" | "choice" | "email" | "reference";
export const RECORD_FIELD_TYPES: RecordFieldType[] = ["text", "longText", "number", "money", "date", "boolean", "choice", "email", "reference"];

export interface RecordFieldOption { value: string; label: string; labelAr?: string | null }
export interface RecordField {
  key: string; label: string; labelAr?: string | null; type: RecordFieldType; required?: boolean; pattern?: string | null;
  min?: number | null; max?: number | null; maxLength?: number | null; options?: RecordFieldOption[] | null; target?: string | null;
  searchable?: boolean; listed?: boolean; help?: string | null; helpAr?: string | null;
}
export interface RecordRelation { key: string; label: string; labelAr?: string | null; target: string; many?: boolean; required?: boolean }
export interface RecordCheck { key: string; when: unknown; messageEn: string; messageAr?: string | null; severity?: "error" | "warning"; fields?: string[] }

export interface RecordTypeBody extends BaseBody {
  fields: RecordField[];
  relations: RecordRelation[];
  rules: RecordCheck[];
  titleField?: string | null;
  numberPrefix: string;
  departmentKey?: string | null;
  extendsServiceKey?: string | null;
  icon: string;
  fieldCount?: number;
}

export interface RecordTypeSummary extends DefinitionSummary {
  fieldCount: number; icon: string; numberPrefix: string; departmentKey?: string | null; extendsServiceKey?: string | null; recordCount: number;
}

export interface DataRecord {
  id: string; recordTypeKey: string; recordTypeVersion: number; number: string; title: string; data: Record<string, unknown> | null;
  caseId?: string | null; departmentKey?: string | null; createdByName?: string | null; createdAt: string; updatedByName?: string | null; updatedAt: string;
  isTest: boolean; isArchived: boolean;
}

export interface RecordIssue { path: string; message: string; messageAr?: string | null; severity: string; keyword?: string | null; ruleKey?: string | null }
export interface RecordLinkInput { relation: string; recordId?: string | null; caseId?: string | null }

export function newRecordTypeBody(name: string, nameAr: string | null): RecordTypeBody {
  const prefix = name.replace(/[^A-Za-z ]/g, "").split(/\s+/).filter(Boolean).map((w) => w[0]!.toUpperCase()).join("").slice(0, 6);
  return { name, nameAr, description: null, descriptionAr: null, tags: [], fields: [], relations: [], rules: [], titleField: null, numberPrefix: prefix.length >= 2 ? prefix : "REC", departmentKey: null, extendsServiceKey: null, icon: "Database" };
}

export function useRecords(params: { type?: string; q?: string; caseId?: string; includeTest?: boolean; includeArchived?: boolean; take?: number }, enabled = true) {
  const api = useApi();
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== "" && v !== false) qs.set(k, String(v)); });
  return useQuery({ queryKey: ["records", "list", params], queryFn: () => api.get<{ total: number; rows: DataRecord[] }>(`/api/records?${qs}`), enabled, staleTime: 5_000 });
}

export function useRecordActions() {
  const api = useApi();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const invalidate = () => { void qc.invalidateQueries({ queryKey: ["records"] }); void qc.invalidateQueries({ queryKey: ["platform", "definitions", "record-types"] }); };
  return {
    /** A check on the server that stores nothing: its failure is not headed "Not saved". */
    validate: useSave({ mutationFn: (input: { type: string; data: Record<string, unknown>; links?: RecordLinkInput[]; locale?: string; version?: number }) => api.post<{ valid: boolean; issues: RecordIssue[] }>("/api/records/validate", input), failed: t("admin:common.error") }),
    create: useSave({ mutationFn: (input: { type: string; data: Record<string, unknown>; caseId?: string | null; links?: RecordLinkInput[]; isTest?: boolean; departmentKey?: string | null; locale?: string }) => api.post<DataRecord>("/api/records", input), onSuccess: invalidate }),
    update: useSave({ mutationFn: (input: { id: string; data: Record<string, unknown>; locale?: string }) => api.put<DataRecord>(`/api/records/${input.id}`, { data: input.data, locale: input.locale }), onSuccess: invalidate }),
    archive: useSave({ mutationFn: (input: { id: string; archived: boolean }) => api.post<DataRecord>(`/api/records/${input.id}/archive`, { archived: input.archived }), onSuccess: invalidate }),
  };
}
